// ─── Render Parity Guard ──────────────────────────────────────
// Diagnostic helper that compares the Studio editor canvas DOM with
// the public render DOM to catch render-path divergence early.
//
// Scope (Phase 1):
//   • Section-v1 elements:   [data-testid^="v1-el-"]
//   • Legacy collage cards:  [data-testid^="collage-el-"]
//
// Compares NORMALIZED bounds (each element's rect / its section's
// rect) so the editor canvas zoom level doesn't false-flag drift.
// Threshold: 2% AND 8px must both fail to flag — keeps the signal
// quiet for sub-pixel rendering noise.

const FLAG_PERCENT = 0.02;   // 2% of section box
const FLAG_PIXELS = 8;       // tolerance floor (px against the bigger render)

// Walk a document and produce a snapshot per element. The snapshot is
// scale-invariant (percent of section rect) so editor zoom vs. public
// 1x rendering doesn't matter. Accepts a Document or any Element as
// the root so callers can scope to just the editor canvas container.
export function snapshotCanvas(root) {
  if (!root) return { elements: [], sectionCount: 0 };
  const sectionEls = root.querySelectorAll('[data-section-type="collage"], [data-section-version="section-v1"]');
  const out = [];
  sectionEls.forEach((section) => {
    const sectionRect = section.getBoundingClientRect();
    if (sectionRect.width === 0 || sectionRect.height === 0) return;
    const items = section.querySelectorAll('[data-testid^="v1-el-"], [data-testid^="collage-el-"]');
    items.forEach((el) => {
      const r = el.getBoundingClientRect();
      const id = el.getAttribute('data-element-id')
        || (el.getAttribute('data-testid') || '').replace(/^v1-el-|^collage-el-/, '')
        || null;
      if (!id) return;
      out.push({
        id,
        sectionId: el.getAttribute('data-section-id') || null,
        type: el.getAttribute('data-element-type') || 'element',
        name: el.getAttribute('data-element-name') || null,
        // Normalized — fraction of the section box (0..1).
        leftPct: (r.left - sectionRect.left) / sectionRect.width,
        topPct: (r.top - sectionRect.top) / sectionRect.height,
        widthPct: r.width / sectionRect.width,
        heightPct: r.height / sectionRect.height,
        // Keep the absolute width/height for pixel-tolerance math.
        absWidth: r.width,
        absHeight: r.height,
      });
    });
  });
  return { elements: out, sectionCount: sectionEls.length };
}

function flagged(deltaPct, deltaAbs) {
  return Math.abs(deltaPct) > FLAG_PERCENT && Math.abs(deltaAbs) > FLAG_PIXELS;
}

// Pair editor and public snapshots by id; return only the elements
// that drift past BOTH thresholds (percent and px).
export function compareSnapshots(editorSnap, publicSnap) {
  const editorById = new Map(editorSnap.elements.map((e) => [e.id, e]));
  const publicById = new Map(publicSnap.elements.map((e) => [e.id, e]));
  const issues = [];

  for (const [id, ed] of editorById) {
    const pu = publicById.get(id);
    if (!pu) {
      // Element exists in editor but not in public render — probably
      // an unsaved element, or a render error. Worth flagging quietly.
      issues.push({
        id,
        sectionId: ed.sectionId,
        type: ed.type,
        name: ed.name,
        kind: 'missing_in_public',
        deltas: null,
      });
      continue;
    }
    // Use the bigger render's absolute size as the pixel reference so
    // an 8px shift on a small thumbnail isn't lost; equally a 1% shift
    // on a hero image isn't ignored.
    const refW = Math.max(ed.absWidth, pu.absWidth);
    const refH = Math.max(ed.absHeight, pu.absHeight);
    const dx = ed.leftPct - pu.leftPct;
    const dy = ed.topPct - pu.topPct;
    const dw = ed.widthPct - pu.widthPct;
    const dh = ed.heightPct - pu.heightPct;
    const dxPx = dx * refW;
    const dyPx = dy * refH;
    const dwPx = dw * refW;
    const dhPx = dh * refH;
    const drifted = flagged(dx, dxPx) || flagged(dy, dyPx) || flagged(dw, dwPx) || flagged(dh, dhPx);
    if (drifted) {
      issues.push({
        id,
        sectionId: ed.sectionId,
        type: ed.type,
        name: ed.name,
        kind: 'drift',
        deltas: {
          leftPct: dx, topPct: dy, widthPct: dw, heightPct: dh,
          leftPx: Math.round(dxPx), topPx: Math.round(dyPx),
          widthPx: Math.round(dwPx), heightPx: Math.round(dhPx),
        },
      });
    }
  }

  return {
    matched: editorById.size > 0 && issues.length === 0,
    editorCount: editorById.size,
    publicCount: publicById.size,
    issues,
  };
}

// Render the public preview in an off-screen iframe, wait for the
// canvas to settle, snapshot, then tear down. Returns { snapshot,
// error }. Never throws — parity check is best-effort.
export async function snapshotPublicRender({ url, timeoutMs = 6000 } = {}) {
  if (!url || typeof document === 'undefined') {
    return { snapshot: null, error: 'no_url' };
  }
  return new Promise((resolve) => {
    let resolved = false;
    const finish = (payload) => {
      if (resolved) return;
      resolved = true;
      try { document.body.removeChild(host); } catch { /* ignore */ }
      resolve(payload);
    };

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:fixed;left:-10000px;top:0;width:390px;height:800px;pointer-events:none;opacity:0;';
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'parity-guard');
    iframe.setAttribute('data-testid', 'parity-guard-iframe');
    iframe.style.cssText = 'width:390px;height:800px;border:0;';
    // Cache-bust so the iframe definitely re-fetches the just-saved
    // data (avoids SW / browser cache hiding a fresh write).
    const sep = url.indexOf('?') >= 0 ? '&' : '?';
    iframe.src = `${url}${sep}_pg=${Date.now()}`;
    host.appendChild(iframe);
    document.body.appendChild(host);

    const ceiling = setTimeout(() => {
      // Even on timeout, try one last snapshot — partial result is
      // better than no signal at all.
      let doc = null;
      try { doc = iframe.contentDocument || iframe.contentWindow?.document || null; } catch { doc = null; }
      finish({ snapshot: doc ? snapshotCanvas(doc) : null, error: doc ? null : 'timeout' });
    }, timeoutMs);

    iframe.addEventListener('load', () => {
      // Give the public renderer ~900ms to mount images + fonts so the
      // bounding rects are stable.
      setTimeout(() => {
        clearTimeout(ceiling);
        let doc = null;
        try { doc = iframe.contentDocument || iframe.contentWindow?.document || null; } catch { doc = null; }
        finish({ snapshot: doc ? snapshotCanvas(doc) : null, error: doc ? null : 'no_doc' });
      }, 900);
    });
  });
}
